import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, Building2, List, Info, Database } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const SecuredAssetsFinancePage = () => {
    const [loading, setLoading] = useState(true);

    // Finance Settings State
    const [financeSettings, setFinanceSettings] = useState({
        section_tag: 'What We Finance',
        section_title: 'Types of Secured Properties We Finance',
        section_description: '',
        assessment_heading: 'Property Assessment Criteria',
        assessment_intro: '',
        is_active: true
    });

    // Property Types State
    const [propertyTypes, setPropertyTypes] = useState([]);
    const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
    const [propertyModalData, setPropertyModalData] = useState({
        icon_name: '',
        title: '',
        description: '',
        color: 'blue',
        display_order: 0,
        is_active: true
    });
    const [editingProperty, setEditingProperty] = useState(null);
    const [propertyDeleteConfirm, setPropertyDeleteConfirm] = useState({ isOpen: false, id: null });

    // Assessment Factors State
    const [factors, setFactors] = useState([]);
    const [isFactorModalOpen, setIsFactorModalOpen] = useState(false);
    const [factorModalData, setFactorModalData] = useState({
        factor_text: '',
        display_order: 0,
        is_active: true
    });
    const [editingFactor, setEditingFactor] = useState(null);
    const [factorDeleteConfirm, setFactorDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, propertyRes, factorsRes] = await Promise.all([
                axios.get('/api/admin/secured-assets/finance-settings'),
                axios.get('/api/admin/secured-assets/property-types'),
                axios.get('/api/admin/secured-assets/assessment-factors')
            ]);

            if (settingsRes.data.success && settingsRes.data.data) {
                setFinanceSettings(settingsRes.data.data);
            }
            if (propertyRes.data.success) {
                setPropertyTypes(propertyRes.data.data || []);
            }
            if (factorsRes.data.success) {
                setFactors(factorsRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFinanceSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSettingsSubmit = async () => {
        try {
            await axios.put('/api/admin/secured-assets/finance-settings', financeSettings);
            toast.success('Finance settings updated');
            fetchData();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        }
    };

    // Property Handlers
    const handleAddProperty = () => {
        setEditingProperty(null);
        setPropertyModalData({
            icon_name: 'Building2',
            title: '',
            description: '',
            color: 'blue',
            display_order: propertyTypes.length + 1,
            is_active: true
        });
        setIsPropertyModalOpen(true);
    };

    const handleEditProperty = (prop) => {
        setEditingProperty(prop);
        setPropertyModalData({
            ...prop,
            is_active: Boolean(prop.is_active)
        });
        setIsPropertyModalOpen(true);
    };

    const handlePropertySubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingProperty) {
                await axios.put(`/api/admin/secured-assets/property-types/${editingProperty.id}`, propertyModalData);
                toast.success('Property type updated');
            } else {
                await axios.post('/api/admin/secured-assets/property-types', propertyModalData);
                toast.success('Property type created');
            }
            setIsPropertyModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving property type:', error);
            toast.error('Failed to save property type');
        }
    };

    const confirmDeleteProperty = async () => {
        try {
            await axios.delete(`/api/admin/secured-assets/property-types/${propertyDeleteConfirm.id}`);
            toast.success('Property type deleted');
            setPropertyDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting property type:', error);
            toast.error('Failed to delete property type');
        }
    };

    // Factor Handlers
    const handleAddFactor = () => {
        setEditingFactor(null);
        setFactorModalData({
            factor_text: '',
            display_order: factors.length + 1,
            is_active: true
        });
        setIsFactorModalOpen(true);
    };

    const handleEditFactor = (factor) => {
        setEditingFactor(factor);
        setFactorModalData({
            ...factor,
            is_active: Boolean(factor.is_active)
        });
        setIsFactorModalOpen(true);
    };

    const handleFactorSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingFactor) {
                await axios.put(`/api/admin/secured-assets/assessment-factors/${editingFactor.id}`, factorModalData);
                toast.success('Factor updated');
            } else {
                await axios.post('/api/admin/secured-assets/assessment-factors', factorModalData);
                toast.success('Factor created');
            }
            setIsFactorModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving factor:', error);
            toast.error('Failed to save factor');
        }
    };

    const confirmDeleteFactor = async () => {
        try {
            await axios.delete(`/api/admin/secured-assets/assessment-factors/${factorDeleteConfirm.id}`);
            toast.success('Factor deleted');
            setFactorDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting factor:', error);
            toast.error('Failed to delete factor');
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading...</div>;

    return (
        <div className="max-w-7xl mx-auto pb-12">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Secured Assets - What We Finance</h1>
                    <p className="text-slate-500 mt-1">Manage property types and assessment criteria</p>
                </div>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
                <div className="flex items-center gap-2 mb-6 text-primary-900 border-b border-slate-50 pb-4">
                    <Info size={20} className="font-bold" />
                    <h2 className="text-lg font-bold">Main Section Settings</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Tag</label>
                        <input
                            type="text"
                            name="section_tag"
                            value={financeSettings.section_tag || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={financeSettings.section_title || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Description</label>
                        <textarea
                            name="section_description"
                            value={financeSettings.section_description || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-24 resize-none"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Assessment Heading</label>
                        <input
                            type="text"
                            name="assessment_heading"
                            value={financeSettings.assessment_heading || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Assessment Intro Text</label>
                        <textarea
                            name="assessment_intro"
                            value={financeSettings.assessment_intro || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-24 resize-none"
                        />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={handleSettingsSubmit}
                        className="px-6 py-2.5 bg-primary-900 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>

            {/* Property Types Management */}
            <div className="mb-12">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-2 text-primary-900">
                        <Building2 size={20} className="font-bold" />
                        <h2 className="text-xl font-bold">Property Type Cards</h2>
                    </div>
                    <button
                        onClick={handleAddProperty}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors shadow-md font-semibold"
                    >
                        <Plus size={18} /> Add Property Type
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {propertyTypes.map((prop) => (
                        <div key={prop.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative group overflow-hidden">
                            <div className="flex justify-between items-start mb-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${prop.color === 'blue' ? 'bg-blue-600' :
                                        prop.color === 'gold' ? 'bg-amber-500' :
                                            prop.color === 'green' ? 'bg-emerald-600' :
                                                prop.color === 'purple' ? 'bg-purple-600' : 'bg-slate-600'
                                    }`}>
                                    <Building2 size={24} />
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={() => handleEditProperty(prop)}
                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                                    >
                                        <Pencil size={16} />
                                    </button>
                                    <button
                                        onClick={() => setPropertyDeleteConfirm({ isOpen: true, id: prop.id })}
                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                            <h3 className="font-bold text-slate-800 mb-2">{prop.title}</h3>
                            <p className="text-sm text-slate-500 leading-relaxed mb-4">{prop.description}</p>
                            <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                                <span className="text-xs font-medium text-slate-400">Order: {prop.display_order}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${prop.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                    {prop.is_active ? 'Active' : 'Hidden'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Assessment Factors Management */}
            <div>
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-2 text-primary-900">
                        <Database size={20} className="font-bold" />
                        <h2 className="text-xl font-bold">Assessment Criteria Factors</h2>
                    </div>
                    <button
                        onClick={handleAddFactor}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors shadow-md font-semibold"
                    >
                        <Plus size={18} /> Add Factor
                    </button>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="px-6 py-4 text-sm font-bold text-slate-600">Order</th>
                                <th className="px-6 py-4 text-sm font-bold text-slate-600">Factor Text</th>
                                <th className="px-6 py-4 text-sm font-bold text-slate-600">Status</th>
                                <th className="px-6 py-4 text-sm font-bold text-slate-600 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {factors.map((factor) => (
                                <tr key={factor.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-4 text-sm text-slate-600">{factor.display_order}</td>
                                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{factor.factor_text}</td>
                                    <td className="px-6 py-4">
                                        <span className={`text-xs px-2 py-1 rounded-full ${factor.is_active ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                            {factor.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => handleEditFactor(factor)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil size={16} /></button>
                                            <button onClick={() => setFactorDeleteConfirm({ isOpen: true, id: factor.id })} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Property Modal */}
            {isPropertyModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 animate-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-slate-800">{editingProperty ? 'Edit Property Type' : 'Add Property Type'}</h2>
                            <button onClick={() => setIsPropertyModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>

                        <form onSubmit={handlePropertySubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Icon (Lucide Name)</label>
                                    <input
                                        type="text"
                                        value={propertyModalData.icon_name}
                                        onChange={(e) => setPropertyModalData({ ...propertyModalData, icon_name: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="Building2, Home, etc."
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Color Theme</label>
                                    <select
                                        value={propertyModalData.color}
                                        onChange={(e) => setPropertyModalData({ ...propertyModalData, color: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    >
                                        <option value="blue">Blue</option>
                                        <option value="gold">Gold</option>
                                        <option value="green">Green</option>
                                        <option value="purple">Purple</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={propertyModalData.title}
                                    onChange={(e) => setPropertyModalData({ ...propertyModalData, title: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                                <textarea
                                    value={propertyModalData.description}
                                    onChange={(e) => setPropertyModalData({ ...propertyModalData, description: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none h-24 resize-none"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={propertyModalData.display_order}
                                        onChange={(e) => setPropertyModalData({ ...propertyModalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        min="1"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={propertyModalData.is_active}
                                            onChange={(e) => setPropertyModalData({ ...propertyModalData, is_active: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        <span className="ml-3 text-sm font-medium text-slate-700">{propertyModalData.is_active ? 'Active' : 'Hidden'}</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button type="button" onClick={() => setIsPropertyModalOpen(false)} className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all">Cancel</button>
                                <button type="submit" className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg">{editingProperty ? 'Update Property' : 'Create Property'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Factor Modal */}
            {isFactorModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 animate-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-slate-800">{editingFactor ? 'Edit Factor' : 'Add Factor'}</h2>
                            <button onClick={() => setIsFactorModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleFactorSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Factor Text</label>
                                <textarea
                                    value={factorModalData.factor_text}
                                    onChange={(e) => setFactorModalData({ ...factorModalData, factor_text: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none h-24 resize-none"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={factorModalData.display_order}
                                        onChange={(e) => setFactorModalData({ ...factorModalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        min="1"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={factorModalData.is_active}
                                            onChange={(e) => setFactorModalData({ ...factorModalData, is_active: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        <span className="ml-3 text-sm font-medium text-slate-700">{factorModalData.is_active ? 'Active' : 'Inactive'}</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button type="button" onClick={() => setIsFactorModalOpen(false)} className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all">Cancel</button>
                                <button type="submit" className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg">{editingFactor ? 'Update Factor' : 'Create Factor'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={propertyDeleteConfirm.isOpen}
                onClose={() => setPropertyDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDeleteProperty}
                title="Delete Property Type"
                message="Are you sure you want to delete this property type? This action cannot be undone."
            />

            <ConfirmDialog
                isOpen={factorDeleteConfirm.isOpen}
                onClose={() => setFactorDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDeleteFactor}
                title="Delete Factor"
                message="Are you sure you want to delete this assessment factor? This action cannot be undone."
            />
        </div>
    );
};

export default SecuredAssetsFinancePage;
